<template>
  <Teleport to="body">
    <!-- Fullscreen "entering guide mode" splash (grouped tours) -->
    <Transition name="guide-splash">
      <TutorialIntroModal
        v-if="introVisible"
        @continue="dismissIntro"
        @skip-all="skipAllFromIntro"
      />
    </Transition>

    <!-- Guide overlay: spotlight + tooltip -->
    <Transition name="guide-fade">
      <div v-if="isActive" class="guide-overlay">
        <!-- Click blocker (dimming comes from the spotlight's shadow) -->
        <div class="guide-overlay__backdrop"></div>

        <!-- Spotlight cutout - transitions smoothly between steps -->
        <div class="guide-overlay__spotlight" :style="spotlightStyle"></div>

        <!-- Step tooltip -->
        <Transition name="guide-tooltip" mode="out-in">
          <TutorialTooltip
            v-if="activeConfig && !isTransitioning"
            ref="tooltipRef"
            :key="activeTutorialId"
            :text="activeConfig.text"
            :importance="activeConfig.importance"
            :placement="tooltipPosition.placement"
            :position="tooltipPosition"
            :step-info="stepInfo"
            @next="nextStep"
            @skip-all="skipAll"
          />
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useTutorial } from '../useTutorial'
import { calculateSpotlightRect, calculateTooltipPosition } from '../tutorialPositioning'
import TutorialTooltip from './TutorialTooltip.vue'
import TutorialIntroModal from './TutorialIntroModal.vue'

const {
  isActive,
  activeElement,
  activeConfig,
  activeTutorialId,
  stepInfo,
  isTransitioning,
  introVisible,
  positionKey,
  nextStep,
  skipAll,
  dismissIntro,
  skipAllFromIntro,
  refreshPosition,
} = useTutorial()

const spotlight = ref({ top: 0, left: 0, width: 0, height: 0, borderRadius: 8 })
const tooltipPosition = ref({
  top: 0,
  left: 0,
  placement: 'bottom',
  arrowPosition: 50,
  maxWidth: 320,
})
const tooltipRef = ref(null)
const tooltipHeight = ref(180)

const spotlightStyle = computed(() => ({
  top: `${spotlight.value.top}px`,
  left: `${spotlight.value.left}px`,
  width: `${spotlight.value.width}px`,
  height: `${spotlight.value.height}px`,
  borderRadius: `${spotlight.value.borderRadius}px`,
}))

function updatePositions() {
  if (!activeElement.value) return

  spotlight.value = calculateSpotlightRect(activeElement.value, 8)
  tooltipPosition.value = calculateTooltipPosition(activeElement.value, tooltipHeight.value)

  // Re-measure after render; reposition if the estimate was off
  nextTick(() => {
    const el = tooltipRef.value?.$el
    if (!el?.offsetHeight) return
    if (Math.abs(el.offsetHeight - tooltipHeight.value) > 10) {
      tooltipHeight.value = el.offsetHeight
      tooltipPosition.value = calculateTooltipPosition(activeElement.value, el.offsetHeight)
    }
  })
}

watch(
  [activeElement, positionKey],
  () => {
    if (activeElement.value) updatePositions()
  },
  { immediate: true }
)

// rAF-throttled reposition on scroll/resize
let rafId = null
function scheduleUpdate() {
  if (!isActive.value || rafId) return
  rafId = requestAnimationFrame(() => {
    rafId = null
    updatePositions()
  })
}

function handleResize() {
  if (isActive.value) refreshPosition()
}

function handleKeydown(e) {
  if (introVisible.value) {
    if (e.key === 'Escape') {
      e.preventDefault()
      skipAllFromIntro()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      dismissIntro()
    }
    return
  }

  if (!isActive.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    skipAll()
  } else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    nextStep()
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', scheduleUpdate, true)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', scheduleUpdate, true)
  window.removeEventListener('keydown', handleKeydown)
  if (rafId) cancelAnimationFrame(rafId)
})
</script>

<style scoped>
.guide-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
}

.guide-overlay__backdrop {
  position: absolute;
  inset: 0;
}

/* Spotlight: the huge shadow dims everything around the cutout,
   and transitions let it glide between steps */
.guide-overlay__spotlight {
  position: fixed;
  pointer-events: none;
  box-shadow:
    0 0 0 200vmax rgba(15, 23, 42, 0.72),
    0 0 0 3px rgba(255, 255, 255, 0.9),
    0 0 24px 6px rgba(59, 130, 246, 0.35);
  transition:
    top 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: spotlightPulse 2.4s ease-in-out infinite;
}

@keyframes spotlightPulse {
  0%,
  100% {
    box-shadow:
      0 0 0 200vmax rgba(15, 23, 42, 0.72),
      0 0 0 3px rgba(255, 255, 255, 0.9),
      0 0 24px 6px rgba(59, 130, 246, 0.35);
  }
  50% {
    box-shadow:
      0 0 0 200vmax rgba(15, 23, 42, 0.72),
      0 0 0 5px rgba(255, 255, 255, 0.95),
      0 0 34px 10px rgba(59, 130, 246, 0.45);
  }
}

/* Overlay fade */
.guide-fade-enter-active,
.guide-fade-leave-active {
  transition: opacity 0.25s ease;
}

.guide-fade-enter-from,
.guide-fade-leave-to {
  opacity: 0;
}

/* Splash fade */
.guide-splash-enter-active {
  transition: opacity 0.3s ease;
}

.guide-splash-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.guide-splash-enter-from {
  opacity: 0;
}

.guide-splash-leave-to {
  opacity: 0;
  transform: scale(1.04);
}

/* Tooltip swap between steps */
.guide-tooltip-enter-active,
.guide-tooltip-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.guide-tooltip-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.guide-tooltip-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .guide-overlay__spotlight {
    animation: none;
    transition: none;
  }
}
</style>
